import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  IconButton,
  Divider,
  Typography,
  LinearProgress,
  Tooltip,
  TextField,
  MenuItem,
  InputAdornment,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import Nodata from "../../assets/errors/nodata.png";

import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/* ─── Shared holiday / working-day helpers ─────────────────────────────── */

const NAMIBIA_HOLIDAYS_RAW = [
  // 2026
  "2026-01-01", // New Year's Day
  "2026-03-21", // Independence Day
  "2026-04-03", // Good Friday
  "2026-04-06", // Easter Monday
  "2026-05-01", // Workers' Day
  "2026-05-04", // Cassinga Day
  "2026-05-14", // Ascension Day
  "2026-05-25", // Africa Day
  "2026-08-26", // Heroes' Day
  "2026-12-10", // Human Rights Day
  "2026-12-25", // Christmas Day
  "2026-12-26", // Day of Goodwill

  // 2027
  "2027-01-01",
  "2027-03-21",
  "2027-04-03",
  "2027-04-06",
  "2027-05-01",
  "2027-05-04",
  "2027-05-14",
  "2027-05-25",
  "2027-08-26",
  "2027-12-10",
  "2027-12-25",
  "2027-12-26",
];

// Shift any Sunday public holiday to the following Monday (as per the Act)
const PUBLIC_HOLIDAYS = new Set(
  NAMIBIA_HOLIDAYS_RAW.map((dateStr) => {
    const d = new Date(dateStr);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  })
);

/**
 * Count working days (Mon–Fri, excluding Namibian public holidays)
 * between two ISO date strings (inclusive).
 */
const countWorkingDays = (startStr, endStr) => {
  if (!startStr || !endStr) return 0;
  const cursor = new Date(startStr);
  const end = new Date(endStr);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return 0;

  cursor.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (cursor > end) return 0;

  let count = 0;
  while (cursor <= end) {
    const day = cursor.getDay();
    const dateStr = cursor.toISOString().split("T")[0];
    if (day !== 0 && day !== 6 && !PUBLIC_HOLIDAYS.has(dateStr)) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

/** First working day strictly after the given ISO date. */
const nextWorkingDay = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  d.setHours(0, 0, 0, 0);
  do {
    d.setDate(d.getDate() + 1);
  } while (
    d.getDay() === 0 ||
    d.getDay() === 6 ||
    PUBLIC_HOLIDAYS.has(d.toISOString().split("T")[0])
  );
  return d.toISOString().split("T")[0];
};

/**
 * Maximum working days allowed per leave type.
 * Leave types not listed here have no enforced cap.
 */
const LEAVE_TYPE_LIMITS = {
  Annual: 4,
  Compassionate: 5,
  Sick: 1,
  Maternity: 40,
};

const LEAVE_TYPES = ["Annual", "Sick", "Compassionate", "Study", "Maternity"];

const DEPARTMENTS = [
  "ICT",
  "SCIENCE",
  "ART",
  "HR",
  "ADMIN",
  "STEAM",
  "ENGINEERING",
  "HYGIENE",
];

/* ─── Roles & status ───────────────────────────────────────────────────── */

const STAGE1_ROLES = ["supervisor", "manager", "ceo", "coo", "supperAdmin"];
const STAGE2_ROLES = ["ceo", "coo", "supperAdmin"];
const MANAGER_ROLES = [
  "admin",
  "hr",
  "supervisor",
  "manager",
  "ceo",
  "coo",
  "supperAdmin",
];

const STATUS_LABEL = {
  0: "Pending",
  1: "In progress",
  2: "Approved",
  3: "Rejected",
  4: "Returned",
};

const STATUS_ORDER = ["all", 0, 1, 2, 3, 4];

// A staff member who is not active has left the organisation.
const isActiveStaff = (s) =>
  s?.status === "active" || s?.status === 1 || s?.status === "1";

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ─────────────────────────────────────────────────────────────────────── */

const Leave = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const [detailLeave, setDetailLeave] = useState(null);

  const [formData, setFormData] = useState({
    staff_id: "",
    leave_type: "",
    reason: "",
    startDate: "",
    endDate: "",
    back_in_office: "",
    department: "",
    comments: "",
    status: "0",
  });

  const canManage = MANAGER_ROLES.includes(userRole);
  const canStage1 = STAGE1_ROLES.includes(userRole);
  const canStage2 = STAGE2_ROLES.includes(userRole);

  /* ---------------- Derived design tokens ---------------- */
  const ui = useMemo(() => {
    const accent = colors.teal[500];
    const accent2 = colors.newTeal[500];
    return {
      accent,
      accent2,
      pageBg: isDark ? colors.dark[800] : "#e9ebef",
      surface: isDark ? colors.dark[700] : colors.white[900],
      surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
      border: isDark
        ? alpha(colors.white[500], 0.09)
        : alpha(colors.dark[500], 0.1),
      textPrimary: colors.primary[100],
      textSecondary: isDark ? colors.gray[400] : colors.gray[300],
      gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
      shadow: isDark
        ? "0 14px 34px -22px rgba(0,0,0,0.8)"
        : "0 14px 34px -24px rgba(16,24,74,0.35)",
    };
  }, [colors, isDark]);

  const statusColor = useCallback(
    (s) =>
      ({
        0: colors.orange[500],
        1: colors.newTeal[500],
        2: colors.green[500],
        3: colors.red[500],
        4: colors.indigo[500],
      }[num(s)] || colors.gray[500]),
    [colors]
  );

  const typeColor = useCallback(
    (type) =>
      ({
        Annual: colors.teal[500],
        Sick: colors.red[500],
        Compassionate: colors.indigo[500],
        Study: colors.newTeal[500],
        Maternity: colors.orange[500],
      }[type] || colors.gray[500]),
    [colors]
  );

  /* ---------------- Data ---------------- */
  const fetchData = useCallback(async () => {
    try {
      const [leavesResponse, usersResponse, leaveCreditsResponse] = await Promise.all([
        getData("proccess.php", "leaves_V2"),
        getData("proccess.php", "staffs"),
        getData("proccess.php", "leaveCredits_V2"),
      ]);

      if (leavesResponse.success) setLeaves(leavesResponse.data || []);
      else toast.error(leavesResponse.message || "Failed to load leaves");

      if (usersResponse.success) setStaffs(usersResponse.data || []);
      else toast.error(usersResponse.message || "Failed to load staff");

      if (leaveCreditsResponse.success) setLeaveCredits(leaveCreditsResponse.data || []);
      else toast.error(leaveCreditsResponse.message || "Failed to load leave credits");
    } catch (err) {
      toast.error("Network error: Failed to fetch data");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeStaffs = useMemo(() => staffs.filter(isActiveStaff), [staffs]);

  const staffById = useMemo(() => {
    const map = new Map();
    staffs.forEach((s) => map.set(String(s.Id), s));
    return map;
  }, [staffs]);

  const findStaff = useCallback((id) => staffById.get(String(id)) || null, [staffById]);

  const staffName = useCallback(
    (id) => {
      const s = findStaff(id);
      return s ? `${s.FirstName} ${s.LastName}` : "—";
    },
    [findStaff]
  );

  const creditFor = useCallback(
    (staffId, leaveType) =>
      leaveCredits.find(
        (c) => String(c.user_id) === String(staffId) && c.leave_type === leaveType
      ) || null,
    [leaveCredits]
  );

  /* ---------------- Visible list: newest first ---------------- */
  const visibleLeaves = useMemo(() => {
    const q = search.trim().toLowerCase();

    return leaves
      // Hide requests belonging to staff who have left the organisation.
      .filter((l) => {
        const s = findStaff(l.staff_id);
        return !s || isActiveStaff(s);
      })
      .filter((l) => statusFilter === "all" || num(l.status) === statusFilter)
      .filter((l) => {
        if (!q) return true;
        return [
          staffName(l.staff_id),
          l.leave_type,
          l.reason,
          l.department,
          l.comments,
          STATUS_LABEL[num(l.status)],
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      // Newest request first: highest id, then latest start date.
      .sort((a, b) => {
        const byId = num(b.id) - num(a.id);
        if (byId !== 0) return byId;
        return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      });
  }, [leaves, search, statusFilter, findStaff, staffName]);

  const statusCounts = useMemo(() => {
    const base = { all: 0, 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    leaves.forEach((l) => {
      const s = findStaff(l.staff_id);
      if (s && !isActiveStaff(s)) return;
      base.all += 1;
      base[num(l.status)] = (base[num(l.status)] || 0) + 1;
    });
    return base;
  }, [leaves, findStaff]);

  /* ---------------- Export ---------------- */
  const exportToExcel = () => {
    if (visibleLeaves.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const exportData = visibleLeaves.map((leave) => ({
        ID: leave.id || "",
        "Staff Name": staffName(leave.staff_id),
        "Leave Type": leave.leave_type || "",
        Reason: leave.reason || "",
        "Start Date": leave.startDate || "",
        "End Date": leave.endDate || "",
        "Working Days": countWorkingDays(leave.startDate, leave.endDate),
        "Back In Office": leave.back_in_office || "",
        Department: leave.department || "",
        "Supervisor Approver": staffName(leave.hr_appr_id),
        "Supervisor Approval Date": leave.hr_appr_date || "",
        "Management Approver": staffName(leave.supervisor_appr_id),
        "Management Approval Date": leave.supervisor_appr_date || "",
        Status: STATUS_LABEL[num(leave.status)] || "Unknown",
        "Check In Date": leave.check_in_date || "",
        "Approved By": staffName(leave.approved_by),
        "Rejected Date": leave.rejected_date || "",
        "Rejected By": staffName(leave.rejected_by),
        Comments: leave.comments || "",
      }));

      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(","),
        ...exportData.map((row) =>
          headers
            .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `leaves_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${visibleLeaves.length} record(s)`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  };

  /* ---------------- Shared response handling ---------------- */
  const applyLeavesResponse = useCallback(
    async (response) => {
      if (Array.isArray(response?.data)) setLeaves(response.data);
      else await fetchData();
    },
    [fetchData]
  );

  /* ---------------- Form handlers ---------------- */
  const handleAddClick = () => {
    setSelectedLeave(null);
    setFormData({
      staff_id: "",
      leave_type: "",
      reason: "",
      startDate: "",
      endDate: "",
      back_in_office: "",
      department: "",
      comments: "",
      status: "0",
    });
    setOpenFormDialog(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setFormData({
      staff_id: leave.staff_id || "",
      leave_type: leave.leave_type || "",
      reason: leave.reason || "",
      startDate: leave.startDate || "",
      endDate: leave.endDate || "",
      back_in_office: leave.back_in_office || "",
      department: leave.department || "",
      comments: leave.comments || "",
      status: String(leave.status ?? "0"),
    });
    setOpenFormDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Suggest the first working day after the leave ends.
      if (name === "endDate") next.back_in_office = nextWorkingDay(value);
      if (name === "staff_id") {
        const s = findStaff(value);
        if (s?.department && !prev.department) next.department = s.department;
      }
      return next;
    });
  };

  const formDays = countWorkingDays(formData.startDate, formData.endDate);
  const formLimit = LEAVE_TYPE_LIMITS[formData.leave_type];
  const formOverLimit = formLimit !== undefined && formDays > formLimit;

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData.staff_id || !formData.leave_type) {
      toast.error("Staff member and leave type are required");
      return;
    }
    if (formData.startDate && formData.endDate && formDays === 0) {
      toast.error("The selected range contains no working days");
      return;
    }
    if (formOverLimit) {
      toast.error(
        `${formData.leave_type} leave cannot exceed ${formLimit} working day${
          formLimit === 1 ? "" : "s"
        }. You selected ${formDays} working day${formDays === 1 ? "" : "s"}.`
      );
      return;
    }

    setSaving(true);
    try {
      const response = selectedLeave
        ? await updateData("proccess.php", "leaves_V2", formData, selectedLeave.id)
        : await addData("proccess.php", "leaves_V2", formData);

      if (response.success) {
        toast.success(selectedLeave ? "Leave updated" : "Leave request added");
        await applyLeavesResponse(response);
        setOpenFormDialog(false);
        setSelectedLeave(null);
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- Approval / rejection ---------------- */

  // Stage 1 — supervisor sign-off (status 0 → 1)
  const handleHrApproveClick = async (leave) => {
    const staff = findStaff(leave.staff_id);
    if (!staff) {
      toast.error("Staff member not found for this request");
      return;
    }

    setBusyAction(true);
    try {
      const creditRes = await getData("proccess.php", "leaveCredits_V2");
      const credit =
        creditRes.success &&
        creditRes.data?.find(
          (c) =>
            String(c.user_id) === String(leave.staff_id) &&
            c.leave_type === leave.leave_type
        );

      if (!credit || num(credit.leftCredit) <= 0) {
        toast.error("Cannot approve: Staff member has no remaining leave credits");
        return;
      }

      const response = await updateData(
        "proccess.php",
        "leaves_V2",
        {
          updType: "hr",
          status: 1,
          hr_appr_id: userID,
          hr_appr_date: new Date().toISOString().split("T")[0],
        },
        leave.id
      );

      if (response.success) {
        toast.success("Supervisor approval recorded");
        await applyLeavesResponse(response);
        setDetailLeave(null);
      } else {
        toast.error(response.message || "Failed to approve leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve leave");
      console.error("Approve error:", err);
    } finally {
      setBusyAction(false);
    }
  };

  // Stage 2 — management sign-off (status 1 → 2) and credit deduction
  const handleSupervisorApproveClick = async (leave) => {
    const creditRecord = creditFor(leave.staff_id, leave.leave_type);

    if (!creditRecord) {
      toast.error("No leave credit record found for this staff member and leave type");
      return;
    }
    if (num(creditRecord.leftCredit) <= 0) {
      toast.error("Cannot approve leave: Staff member has no remaining leave credits");
      return;
    }

    const duration = countWorkingDays(leave.startDate, leave.endDate);
    if (duration > num(creditRecord.leftCredit)) {
      toast.error(
        `Cannot approve: Requested ${duration} working day(s) but only ${creditRecord.leftCredit} credit(s) remaining`
      );
      return;
    }

    setBusyAction(true);
    try {
      const response = await updateData(
        "proccess.php",
        "leaves_V2",
        {
          updType: "spvsr",
          status: 2,
          supervisor_appr_id: userID,
          supervisor_appr_date: new Date().toISOString().split("T")[0],
        },
        leave.id
      );

      if (!response.success) {
        toast.error(response.message || "Failed to approve leave");
        return;
      }

      toast.success("Leave approved");
      await applyLeavesResponse(response);
      setDetailLeave(null);

      const newUsedUp = num(creditRecord.usedUpCredit) + duration;
      const newLeft = num(creditRecord.leaveCredit) - newUsedUp;

      const creditResponse = await updateData(
        "proccess.php",
        "leaveCredits_V2",
        { usedUpCredit: newUsedUp, leftCredit: newLeft },
        creditRecord.id
      );

      if (creditResponse.success) {
        setLeaveCredits((prev) =>
          prev.map((c) =>
            c.id === creditRecord.id
              ? { ...c, usedUpCredit: newUsedUp, leftCredit: newLeft }
              : c
          )
        );
      } else {
        toast.error(creditResponse.message || "Failed to update leave credits");
      }
    } catch (err) {
      toast.error("Network error: Failed to approve leave");
      console.error("Approve error:", err);
    } finally {
      setBusyAction(false);
    }
  };

  // Return to office (status 2 → 4)
  const handleApproveClick = async (leave) => {
    const now = Date.now();
    const backInOfficeTime = new Date(leave.back_in_office).getTime();
    const isTodayBack =
      new Date(leave.back_in_office).toDateString() === new Date().toDateString();
    const isLate = backInOfficeTime < now && !isTodayBack;

    if (backInOfficeTime > now) {
      toast.error("The staff is still on leave!");
      return;
    }

    const daysLate = Math.floor((now - backInOfficeTime) / (1000 * 60 * 60 * 24));
    const comments = isLate
      ? `Staff returned late by ${daysLate} day(s).`
      : "Welcome back.";

    setBusyAction(true);
    try {
      const response = await updateData(
        "proccess.php",
        "leaves_V2",
        {
          updType: "appr",
          status: 4,
          comments,
          approved_by: userID,
          check_in_date: new Date().toISOString().split("T")[0],
        },
        leave.id
      );

      if (response.success) {
        toast.success("Return to office recorded");
        await applyLeavesResponse(response);
        setDetailLeave(null);
      } else {
        toast.error(response.message || "Failed to record return");
      }
    } catch (err) {
      toast.error("Network error: Failed to record return");
      console.error("Approve error:", err);
    } finally {
      setBusyAction(false);
    }
  };

  const handleRejectClick = async (leave) => {
    setBusyAction(true);
    try {
      const response = await updateData(
        "proccess.php",
        "leaves_V2",
        {
          updType: "reject",
          status: 3,
          rejected_by: userID,
          rejected_date: new Date().toISOString().split("T")[0],
        },
        leave.id
      );

      if (response.success) {
        toast.success("Leave rejected");
        await applyLeavesResponse(response);
        setDetailLeave(null);
      } else {
        toast.error(response.message || "Failed to reject leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to reject leave");
      console.error("Reject error:", err);
    } finally {
      setBusyAction(false);
    }
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData("proccess.php", "leaves_V2", leaveToDelete.id);
      if (response.success) {
        toast.success("Leave deleted");
        setLeaves((prev) => prev.filter((l) => l.id !== leaveToDelete.id));
        setDetailLeave(null);
      } else {
        toast.error(response.message || "Failed to delete leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete leave");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  /* ---------------- Action availability ---------------- */
  const actionsFor = useCallback(
    (leave) => {
      const s = num(leave?.status);
      const backTime = new Date(leave?.back_in_office).getTime();
      return {
        stage1: canStage1 && s === 0,
        stage2: canStage2 && s === 1,
        returned: canManage && s === 2 && !(backTime > Date.now()),
        reject: canManage && (s === 0 || s === 1),
        edit: canManage && s < 2,
        remove: canManage && (s === 0 || s === 3),
      };
    },
    [canStage1, canStage2, canManage]
  );

  /* ---------------- Small pieces ---------------- */
  const avatarSrc = (staff) =>
    staff?.image
      ? `https://mindsinaction.com.na/assets/media/pictures/${staff.image}`
      : "";

  const initials = (staff) =>
    `${staff?.FirstName?.[0] || ""}${staff?.LastName?.[0] || ""}`.toUpperCase() || "?";

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      color: ui.textPrimary,
      bgcolor: ui.surfaceAlt,
      "& fieldset": { borderColor: ui.border },
      "&:hover fieldset": { borderColor: alpha(ui.accent, 0.5) },
      "&.Mui-focused fieldset": { borderColor: ui.accent },
    },
    "& .MuiInputLabel-root": { color: ui.textSecondary },
    "& .MuiInputLabel-root.Mui-focused": { color: ui.accent },
    "& .MuiFormHelperText-root": { color: ui.textSecondary },
  };

  const primaryBtn = {
    px: 2.75,
    py: 1.1,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color: "#ffffff",
    background: ui.gradient,
    boxShadow: `0 10px 24px -12px ${alpha(ui.accent, 0.9)}`,
    "&:hover": { filter: "brightness(1.06)" },
  };

  const outlineBtn = (color) => ({
    px: 2.5,
    py: 1.05,
    borderRadius: 2,
    fontWeight: 700,
    textTransform: "none",
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
    "&:hover": { bgcolor: alpha(color, 0.1), borderColor: color },
  });

  const DetailRow = ({ label, value, color }) => (
    <Box sx={{ mb: 1.75 }}>
      <Typography
        variant="h6"
        sx={{
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: ui.textSecondary,
          mb: 0.3,
        }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, color: color || ui.textPrimary }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  /* ------------------------------------------------------------------ */
  const detailStaff = detailLeave ? findStaff(detailLeave.staff_id) : null;
  const detailCredit = detailLeave
    ? creditFor(detailLeave.staff_id, detailLeave.leave_type)
    : null;
  const detailDays = detailLeave
    ? countWorkingDays(detailLeave.startDate, detailLeave.endDate)
    : 0;
  const detailActions = detailLeave ? actionsFor(detailLeave) : {};

  return (
    <Box sx={{ bgcolor: ui.pageBg, minHeight: "100%", pb: 4 }}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <Container maxWidth="xl" sx={{ py: 3.5 }}>
        {/* ------------------------------------------------------------ */}
        {/* Header                                                        */}
        {/* ------------------------------------------------------------ */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h2"
              sx={{ fontWeight: 800, letterSpacing: -0.4, color: ui.textPrimary }}
            >
              Staff Leave Requests
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
              Newest requests first — click any card for full details and actions
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              placeholder="Search name, type, reason…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ ...fieldSx, minWidth: { sm: 260 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 19, color: ui.textSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              sx={outlineBtn(colors.green[500])}
            >
              Export
            </Button>
            <Button startIcon={<AddIcon />} onClick={handleAddClick} sx={primaryBtn}>
              Add request
            </Button>
          </Stack>
        </Stack>

        {/* ------------------------------------------------------------ */}
        {/* Status filter                                                 */}
        {/* ------------------------------------------------------------ */}
        <ToggleButtonGroup
          exclusive
          value={statusFilter}
          onChange={(_, v) => v !== null && setStatusFilter(v)}
          sx={{
            mb: 3,
            flexWrap: "wrap",
            gap: 1,
            "& .MuiToggleButton-root": {
              px: 2,
              py: 0.75,
              border: `1px solid ${ui.border} !important`,
              borderRadius: "999px !important",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              color: ui.textSecondary,
              bgcolor: ui.surface,
              "&.Mui-selected": {
                color: ui.accent,
                bgcolor: alpha(ui.accent, 0.14),
                borderColor: `${alpha(ui.accent, 0.5)} !important`,
                "&:hover": { bgcolor: alpha(ui.accent, 0.2) },
              },
            },
          }}
        >
          {STATUS_ORDER.map((key) => (
            <ToggleButton key={String(key)} value={key}>
              {key === "all" ? "All" : STATUS_LABEL[key]}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.85,
                  borderRadius: 999,
                  fontSize: 11,
                  bgcolor: alpha(
                    key === "all" ? ui.textSecondary : statusColor(key),
                    0.18
                  ),
                  color: key === "all" ? ui.textSecondary : statusColor(key),
                }}
              >
                {statusCounts[key] || 0}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* ------------------------------------------------------------ */}
        {/* Cards                                                         */}
        {/* ------------------------------------------------------------ */}
        {loading ? (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`sk-${i}`}>
                <Skeleton
                  variant="rounded"
                  height={232}
                  sx={{ borderRadius: 3, bgcolor: alpha(ui.textSecondary, 0.12) }}
                />
              </Grid>
            ))}
          </Grid>
        ) : visibleLeaves.length === 0 ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 10 }}>
            <Box component="img" src={Nodata} alt="No data" sx={{ width: 150, opacity: 0.85 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
              No leave requests
            </Typography>
            <Typography variant="h6" sx={{ color: ui.textSecondary }}>
              {search || statusFilter !== "all"
                ? "Nothing matches the current filters."
                : "Requests will appear here once staff submit them."}
            </Typography>
          </Stack>
        ) : (
          <Grid container spacing={2.5}>
            {visibleLeaves.map((leave) => {
              const staff = findStaff(leave.staff_id);
              const sColor = statusColor(leave.status);
              const tColor = typeColor(leave.leave_type);
              const days = countWorkingDays(leave.startDate, leave.endDate);
              const credit = creditFor(leave.staff_id, leave.leave_type);
              const shortOnCredit =
                credit && days > num(credit.leftCredit) && num(leave.status) < 2;

              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={leave.id}>
                  <Paper
                    elevation={0}
                    onClick={() => setDetailLeave(leave)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDetailLeave(leave);
                      }
                    }}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      borderRadius: 3,
                      overflow: "hidden",
                      position: "relative",
                      bgcolor: ui.surface,
                      border: `1px solid ${ui.border}`,
                      boxShadow: ui.shadow,
                      transition: "transform .2s ease, border-color .2s ease, box-shadow .2s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        insetInlineStart: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        bgcolor: sColor,
                      },
                      "&:hover, &:focus-visible": {
                        outline: "none",
                        transform: "translateY(-4px)",
                        borderColor: alpha(sColor, 0.5),
                        boxShadow: `0 16px 32px -20px ${alpha(sColor, 0.9)}`,
                      },
                    }}
                  >
                    <Box sx={{ p: 2.5, pl: 3 }}>
                      {/* Staff + status */}
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                        <Avatar
                          src={avatarSrc(staff)}
                          sx={{
                            width: 46,
                            height: 46,
                            fontWeight: 700,
                            bgcolor: ui.surfaceAlt,
                            color: ui.textPrimary,
                          }}
                        >
                          {initials(staff)}
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="h4"
                            noWrap
                            sx={{ fontWeight: 700, color: ui.textPrimary }}
                          >
                            {staff ? `${staff.FirstName} ${staff.LastName}` : "Unknown staff"}
                          </Typography>
                          <Typography variant="h6" noWrap sx={{ color: ui.textSecondary }}>
                            {leave.department || staff?.department || "No department"}
                            {leave.id ? ` · #${leave.id}` : ""}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={STATUS_LABEL[num(leave.status)] || "Unknown"}
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: sColor,
                            bgcolor: alpha(sColor, 0.14),
                          }}
                        />
                      </Stack>

                      {/* Dates */}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{
                          p: 1.5,
                          mb: 1.75,
                          borderRadius: 2,
                          bgcolor: alpha(ui.surfaceAlt, isDark ? 0.55 : 0.75),
                        }}
                      >
                        <Box sx={{ textAlign: "center", flex: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                            {fmtDate(leave.startDate)}
                          </Typography>
                          <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                            Start
                          </Typography>
                        </Box>

                        <ArrowRightAltIcon sx={{ color: ui.textSecondary }} />

                        <Box sx={{ textAlign: "center", flex: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                            {fmtDate(leave.endDate)}
                          </Typography>
                          <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                            End
                          </Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: ui.border }} />

                        <Box sx={{ textAlign: "center", minWidth: 62 }}>
                          <Typography variant="h3" sx={{ fontWeight: 800, color: sColor }}>
                            {days}
                          </Typography>
                          <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                            {days === 1 ? "day" : "days"}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Type + reason */}
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={leave.leave_type || "—"}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: tColor,
                            bgcolor: alpha(tColor, 0.14),
                          }}
                        />
                        {shortOnCredit && (
                          <Tooltip
                            title={`Requested ${days} day(s) but only ${credit.leftCredit} credit(s) remain`}
                          >
                            <Chip
                              size="small"
                              icon={<WarningAmberRoundedIcon />}
                              label="Over credit"
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: "0.68rem",
                                color: colors.red[500],
                                bgcolor: alpha(colors.red[500], 0.14),
                                "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>

                      <Typography
                        variant="h6"
                        sx={{
                          color: ui.textSecondary,
                          lineHeight: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: 34,
                        }}
                      >
                        {leave.reason || "No reason provided."}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      {/* -------------------------------------------------------------- */}
      {/* Detail dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={!!detailLeave}
        onClose={() => setDetailLeave(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        {detailLeave && (
          <>
            <Box
              sx={{
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
                background: `linear-gradient(180deg, ${alpha(
                  statusColor(detailLeave.status),
                  isDark ? 0.16 : 0.1
                )} 0%, transparent 100%)`,
              }}
            >
              <Avatar
                src={avatarSrc(detailStaff)}
                sx={{
                  width: 60,
                  height: 60,
                  fontWeight: 700,
                  bgcolor: ui.surfaceAlt,
                  color: ui.textPrimary,
                  border: `2px solid ${alpha(statusColor(detailLeave.status), 0.5)}`,
                }}
              >
                {initials(detailStaff)}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
                  {detailStaff
                    ? `${detailStaff.FirstName} ${detailStaff.LastName}`
                    : "Unknown staff"}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                  <Chip
                    size="small"
                    label={STATUS_LABEL[num(detailLeave.status)] || "Unknown"}
                    sx={{
                      fontWeight: 700,
                      color: statusColor(detailLeave.status),
                      bgcolor: alpha(statusColor(detailLeave.status), 0.14),
                    }}
                  />
                  <Chip
                    size="small"
                    label={detailLeave.leave_type || "—"}
                    sx={{
                      fontWeight: 700,
                      color: typeColor(detailLeave.leave_type),
                      bgcolor: alpha(typeColor(detailLeave.leave_type), 0.14),
                    }}
                  />
                  <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                    Request #{detailLeave.id}
                  </Typography>
                </Stack>
              </Box>

              <IconButton
                onClick={() => setDetailLeave(null)}
                sx={{
                  color: ui.textSecondary,
                  "&:hover": { color: colors.red[500], bgcolor: alpha(colors.red[500], 0.12) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogContent>
              {/* Duration strip */}
              <Stack
                direction="row"
                divider={<Divider orientation="vertical" flexItem sx={{ borderColor: ui.border }} />}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2.5,
                  bgcolor: alpha(ui.surfaceAlt, isDark ? 0.55 : 0.75),
                }}
              >
                {[
                  { label: "Start", value: fmtDate(detailLeave.startDate) },
                  { label: "End", value: fmtDate(detailLeave.endDate) },
                  {
                    label: "Working days",
                    value: detailDays,
                    color: statusColor(detailLeave.status),
                  },
                  { label: "Back in office", value: fmtDate(detailLeave.back_in_office) },
                ].map((item) => (
                  <Box key={item.label} sx={{ flex: 1, textAlign: "center" }}>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 800, color: item.color || ui.textPrimary }}
                    >
                      {item.value}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        mt: 0.4,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        color: ui.textSecondary,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              {/* Credit summary */}
              {detailCredit && (
                <Box
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2.5,
                    border: `1px solid ${ui.border}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ color: ui.textSecondary }}>
                      {detailLeave.leave_type} credits
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: ui.textPrimary }}>
                      {num(detailCredit.usedUpCredit)} used ·{" "}
                      {num(detailCredit.leftCredit)} left of{" "}
                      {num(detailCredit.leaveCredit)}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={
                      num(detailCredit.leaveCredit)
                        ? Math.min(
                            100,
                            (num(detailCredit.usedUpCredit) /
                              num(detailCredit.leaveCredit)) *
                              100
                          )
                        : 0
                    }
                    sx={{
                      height: 7,
                      borderRadius: 999,
                      bgcolor: alpha(typeColor(detailLeave.leave_type), 0.18),
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        backgroundColor: typeColor(detailLeave.leave_type),
                      },
                    }}
                  />
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <DetailRow label="Department" value={detailLeave.department} />
                  <DetailRow label="Reason" value={detailLeave.reason} />
                  <DetailRow label="Comments" value={detailLeave.comments} />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <DetailRow
                    label="Supervisor approval"
                    value={
                      detailLeave.hr_appr_id
                        ? `${staffName(detailLeave.hr_appr_id)} — ${fmtDate(
                            detailLeave.hr_appr_date
                          )}`
                        : "Awaiting"
                    }
                    color={detailLeave.hr_appr_id ? colors.green[500] : ui.textSecondary}
                  />
                  <DetailRow
                    label="Management approval"
                    value={
                      detailLeave.supervisor_appr_id
                        ? `${staffName(detailLeave.supervisor_appr_id)} — ${fmtDate(
                            detailLeave.supervisor_appr_date
                          )}`
                        : "Awaiting"
                    }
                    color={
                      detailLeave.supervisor_appr_id ? colors.green[500] : ui.textSecondary
                    }
                  />
                  {num(detailLeave.status) === 3 ? (
                    <DetailRow
                      label="Rejected"
                      value={`${staffName(detailLeave.rejected_by)} — ${fmtDate(
                        detailLeave.rejected_date
                      )}`}
                      color={colors.red[500]}
                    />
                  ) : (
                    <DetailRow
                      label="Checked in"
                      value={
                        detailLeave.check_in_date
                          ? `${staffName(detailLeave.approved_by)} — ${fmtDate(
                              detailLeave.check_in_date
                            )}`
                          : "Not yet"
                      }
                      color={
                        detailLeave.check_in_date ? colors.indigo[500] : ui.textSecondary
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </DialogContent>

            <Divider sx={{ borderColor: ui.border }} />

            <DialogActions sx={{ px: 3, py: 2.5, flexWrap: "wrap", gap: 1 }}>
              {detailActions.remove && (
                <Button
                  startIcon={<DeleteForeverOutlinedIcon />}
                  onClick={() => handleDeleteClick(detailLeave)}
                  sx={{ ...outlineBtn(colors.red[500]), mr: "auto" }}
                >
                  Delete
                </Button>
              )}

              {detailActions.edit && (
                <Button
                  startIcon={<EditNoteOutlinedIcon />}
                  onClick={() => {
                    setDetailLeave(null);
                    handleEditClick(detailLeave);
                  }}
                  sx={outlineBtn(ui.accent2)}
                >
                  Edit
                </Button>
              )}

              {detailActions.reject && (
                <Button
                  startIcon={<ClearOutlinedIcon />}
                  disabled={busyAction}
                  onClick={() => handleRejectClick(detailLeave)}
                  sx={outlineBtn(colors.red[500])}
                >
                  Reject
                </Button>
              )}

              {detailActions.stage1 && (
                <Button
                  startIcon={<CheckOutlinedIcon />}
                  disabled={busyAction}
                  onClick={() => handleHrApproveClick(detailLeave)}
                  sx={primaryBtn}
                >
                  Supervisor approve
                </Button>
              )}

              {detailActions.stage2 && (
                <Button
                  startIcon={<HowToRegOutlinedIcon />}
                  disabled={busyAction}
                  onClick={() => handleSupervisorApproveClick(detailLeave)}
                  sx={primaryBtn}
                >
                  Management approve
                </Button>
              )}

              {detailActions.returned && (
                <Button
                  startIcon={<EventBusyOutlinedIcon />}
                  disabled={busyAction}
                  onClick={() => handleApproveClick(detailLeave)}
                  sx={primaryBtn}
                >
                  Confirm return
                </Button>
              )}

              <Button
                onClick={() => setDetailLeave(null)}
                sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Add / Edit dialog                                               */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setSelectedLeave(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            {selectedLeave ? "Edit leave request" : "New leave request"}
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, color: ui.textSecondary }}>
            Weekends and Namibian public holidays are excluded automatically.
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1.5 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  id="staff_id"
                  name="staff_id"
                  label="Staff member"
                  value={formData.staff_id}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select staff…</em>
                  </MenuItem>
                  {activeStaffs.map((staff) => (
                    <MenuItem key={staff.Id} value={staff.Id}>
                      {staff.FirstName} {staff.LastName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  id="leave_type"
                  name="leave_type"
                  label="Leave type"
                  value={formData.leave_type}
                  onChange={handleFormChange}
                  required
                  helperText={
                    formLimit !== undefined
                      ? `Maximum ${formLimit} working day${formLimit === 1 ? "" : "s"}`
                      : " "
                  }
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select type…</em>
                  </MenuItem>
                  {LEAVE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box
                          sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: typeColor(t) }}
                        />
                        <span>{t}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  id="reason"
                  name="reason"
                  label="Reason"
                  value={formData.reason}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  id="startDate"
                  name="startDate"
                  label="Start date"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  id="endDate"
                  name="endDate"
                  label="End date"
                  value={formData.endDate}
                  onChange={handleFormChange}
                  required
                  error={formOverLimit}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: formData.startDate || undefined }}
                  helperText={
                    formData.startDate && formData.endDate
                      ? `${formDays} working day${formDays === 1 ? "" : "s"}${
                          formOverLimit ? ` — exceeds ${formLimit}-day limit` : ""
                        }`
                      : " "
                  }
                  sx={{
                    ...fieldSx,
                    ...(formOverLimit && {
                      "& .MuiFormHelperText-root": { color: colors.red[500] },
                    }),
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  fullWidth
                  type="date"
                  id="back_in_office"
                  name="back_in_office"
                  label="Back in office"
                  value={formData.back_in_office}
                  onChange={handleFormChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText="Auto-set to the next working day"
                  sx={fieldSx}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: selectedLeave ? 6 : 12 }}>
                <TextField
                  select
                  fullWidth
                  id="department"
                  name="department"
                  label="Department"
                  value={formData.department}
                  onChange={handleFormChange}
                  required
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>Select department…</em>
                  </MenuItem>
                  {DEPARTMENTS.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {selectedLeave && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    id="comments"
                    name="comments"
                    label="Comments"
                    value={formData.comments}
                    onChange={handleFormChange}
                    sx={fieldSx}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => {
              setOpenFormDialog(false);
              setSelectedLeave(null);
            }}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || formOverLimit}
            endIcon={<SendIcon />}
            sx={primaryBtn}
          >
            {saving ? "Saving…" : selectedLeave ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------------------------------------------------- */}
      {/* Delete dialog                                                   */}
      {/* -------------------------------------------------------------- */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: ui.surface,
            border: `1px solid ${ui.border}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h3" sx={{ fontWeight: 800, color: ui.textPrimary }}>
            Delete leave request
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h5" sx={{ color: ui.textSecondary, lineHeight: 1.6 }}>
            Permanently delete the{" "}
            <Box component="span" sx={{ fontWeight: 800, color: ui.textPrimary }}>
              {leaveToDelete?.leave_type}
            </Box>{" "}
            request for{" "}
            <Box component="span" sx={{ fontWeight: 800, color: ui.textPrimary }}>
              {staffName(leaveToDelete?.staff_id)}
            </Box>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ textTransform: "none", fontWeight: 700, color: ui.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
            sx={{
              px: 2.75,
              py: 1.1,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.red[500],
              "&:hover": { bgcolor: colors.red[600] },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Leave;